import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import MessageList from "../Message/Common/MessageList";
import { FolderType, MessageSource } from "src/Core/Common/Enums";
import { initialPagination } from "src/Core/Common/Constants";
import { ApplicationState } from "src/store";
import { actionTypes as receivedMessageAction } from "../../types/receivedMessageTypes";
import { initialState } from "src/reducers/receivedMessageReducers";
import { IFolderState } from "src/reducers/folderReducer";
import { moveToFolder, printMessages, fetchFolderMessages } from "src/actionCreators/receivedMessageActionCreators";
import { initialMessage, ISentMessageModel } from "src/Core/ViewModels/Message/SentMessageModel";
import { IReceivedMessageModel, initialReceivedMessage } from "src/Core/ViewModels/Message/ReceivedMessageModel";
import { IFileModel } from "src/Core/ViewModels/File/FileModel";
import MessageDetail from "../Message/Common/MessageDetail";
import { IReceivedMailMessage } from "src/Core/ViewModels/Message/ReceivedMailMessageModel";
import { appInsightsData } from "src/helper/AppInsightsData";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { IMessageResponseModel } from "src/Core/ViewModels/Message/MessageResponseModel";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import {
    getThreadedMessageDetail,
    handleChangeExpiryThreadMessages,
    handleDeleteThreadMessages,
    handleThreadsDownloadAllAttachments,
    handleThreadsDownloadSingleAttachment,
} from "src/helper/MessageHelperThreads";

export interface IArchiveMessageProps {
    folderId: number;
    folder?: IFolderModel;
    reload?: boolean;
    handleFolderRefreshClick?: () => void;
    isFolderRefresh?: boolean;
    goToParentFolder?: (data: boolean) => void;
}

const ArchiveMessage: React.FC<IArchiveMessageProps> = props => {
    const [isMessageDetailShow, toggleMessageDetailShow] = useState(false);
    const [pagination, setPagination] = useState(initialPagination);
    const [resetSelection, setSelection] = useState<boolean>(false);
    const dispatch = useDispatch();
    const messageState = useSelector((state: ApplicationState) => state.receivedMessageState);
    const [selectedMessage, setSelectedMessage] = useState<ISentMessageModel | IReceivedMessageModel>(initialMessage);
    const [reloadSortFilter, setReloadSortFilter] = useState(false);
    const sentMessageState = useSelector((state: ApplicationState) => state.sentMessageState);
    useEffect(() => {
        return () => {
            dispatch({ type: receivedMessageAction.RESET_MESSAGES_ACTION, payload: initialState });
        };
    }, []);

    useEffect(() => {
        if (props.reload) {
            setSelection(true);
        }
    }, [props.reload]);

    const folderState: IFolderState = useSelector((appState: ApplicationState) => appState.folderState);

    const fetchArchiveMessages = (
        pageNo: number,
        pageSize: number,
        sortColumn?: string,
        sortOrder?: string,
        search?: string
    ) => {
        dispatch(
            fetchFolderMessages(
                props.folderId,
                pageNo,
                pageSize,
                sortColumn,
                sortOrder,
                search,
                onFetchInboxMessagesCallBack
            )
        );
    };

    const onFetchInboxMessagesCallBack = (result: IMessageResponseModel<IReceivedMessageModel>) => {
        return;
    };

    const handleMessageDetailView = (result: boolean) => {
        if (result) toggleMessageDetailShow(false);
    };

    const handleGetMessageDetail = (message: IReceivedMessageModel) => {
        getThreadedMessageDetail(dispatch, message, messageDetailCallback, FolderType.ARCHIVE);
    };

    const messageDetailCallback = (result: IReceivedMailMessage) => {
        if (result.messageDetail) toggleMessageDetailShow(true);
        else toggleMessageDetailShow(false);
    };

    const handleOnChangeExpiry = (
        expiresDate: Date,
        message: ISentMessageModel | IReceivedMessageModel,
        callback?: (result: boolean) => void
    ): void => {
        handleChangeExpiryThreadMessages(dispatch, expiresDate, message, FolderType.ARCHIVE, callback);
    };

    const checkFolderExpiry = (folderId: number): boolean => {
        let destinationFolderExpiryDays: number | undefined;
        let sourceFolderExpiryDays: number | undefined;
        if (!folderState.foldersData.isLoading) {
            destinationFolderExpiryDays = folderState.foldersData.folders?.find(
                f => f.folderId === folderId
            )?.expiresDays;
            sourceFolderExpiryDays = folderState.foldersData.folders?.find(
                f => f.folderId === props.folderId
            )?.expiresDays;
            if (destinationFolderExpiryDays && sourceFolderExpiryDays) {
                return destinationFolderExpiryDays >= sourceFolderExpiryDays;
            }
        }
        return true;
    };

    const handleDownloadSingleAttachment = (
        fileId: number,
        physicalPath: string,
        messageId: number,
        fileName: string,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        folderId: number
    ) => {
        handleThreadsDownloadSingleAttachment(
            dispatch,
            fileId,
            physicalPath,
            messageId,
            fileName,
            senderEmail,
            receivedMessageGuid,
            messageSource,
            folderId,
            (result: any) => {
                if (result) {
                    window.open(result, "_blank");
                }
            }
        );
    };

    const handleDownloadAllAttachments = (
        files: IFileModel[],
        messageId: number,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        folderId: number
    ) => {
        handleThreadsDownloadAllAttachments(
            dispatch,
            files,
            messageId,
            senderEmail,
            receivedMessageGuid,
            messageSource,
            folderId,
            (result: string) => {
                if (result) {
                    window.open(result, "_blank");
                }
            }
        );
    };

    const handleOnDeleteMessage = (messages: IReceivedMessageModel[], callback: any) => {
        handleDeleteThreadMessages(dispatch, messages, callback, FolderType.ARCHIVE);
    };

    const handleOnPrint = (): void => {
        dispatch(printMessages());
    };

    const handleResetSearchAndSort = () => {
        setReloadSortFilter(value => !value);
    };

    const handleOnMoveToFolder = (messages: IReceivedMessageModel[], folderId: number, callback?: any): void => {
        dispatch(
            moveToFolder(
                messages,
                props.folderId,
                folderId,
                callback,
                appInsightsData.Folders.MyFiles.PageTitle,
                ResourceIdLocators.MyFiles.MsgListBtnMoveMessage
            )
        );
    };

    return (
        <div className="flex-row fixed-height-wapper search-box-no-border-radius">
            <div className="message-list-wrapper">
                <MessageList
                    folderId={props.folderId}
                    folderType={FolderType.ARCHIVE}
                    folder={props?.folder}
                    isLoading={messageState.isLoading}
                    messages={messageState.data.messages}
                    totalRecords={messageState.data.totalRecords}
                    fetchMessages={fetchArchiveMessages}
                    deleteMessages={handleOnDeleteMessage}
                    getMessageDetail={handleGetMessageDetail}
                    print={handleOnPrint}
                    checkFolderExpiry={checkFolderExpiry}
                    showMessageDetail={() => toggleMessageDetailShow(true)}
                    hideMessageDetailView={handleMessageDetailView}
                    pagination={pagination}
                    setPagination={setPagination}
                    moveToFolder={handleOnMoveToFolder}
                    resourceLocators={ResourceIdLocators.MyFiles}
                    clearCheckBoxSelection={resetSelection}
                    setSelection={setSelection}
                    reload={props.reload || reloadSortFilter}
                    handleFolderRefreshClick={props?.handleFolderRefreshClick}
                    isFolderRefresh={props.isFolderRefresh}
                    selectedMessage={selectedMessage}
                    setSelectedMessage={setSelectedMessage}
                />
                {isMessageDetailShow && (
                    <MessageDetail
                        message={
                            selectedMessage.folderId === FolderType.SENT
                                ? sentMessageState.mailData.data
                                : messageState.mailData.data
                        }
                        downloadSingleAttachment={handleDownloadSingleAttachment}
                        downloadAllAttachments={handleDownloadAllAttachments}
                        messageId={selectedMessage.receivedMessageId || initialReceivedMessage.receivedMessageId}
                        folderType={selectedMessage.folderId === FolderType.SENT ? FolderType.SENT : FolderType.ARCHIVE}
                        hideMessageDetailView={handleMessageDetailView}
                        getMessageDetail={handleGetMessageDetail}
                        changeExpiry={handleOnChangeExpiry}
                        pagination={pagination}
                        fetchMessages={fetchArchiveMessages}
                        hideMessageDetail={handleMessageDetailView}
                        resourceLocators={
                            selectedMessage.folderId === FolderType.SENT
                                ? ResourceIdLocators.Sent
                                : ResourceIdLocators.MyFiles
                        }
                        resetSelection={setSelection}
                        folder={props?.folder}
                        resetSearchAndSort={handleResetSearchAndSort}
                        deleteFromArchive={true}
                        currentFolderId={props.folderId}
                    />
                )}
            </div>
        </div>
    );
};

export default ArchiveMessage;
